import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "@/lib/router-navigation";
import { axiosInstance } from "@/helpers";
import {
  fetchBffSession,
  startBffLogin,
  logoutBffSession,
} from "@/helpers/bff-auth.helper";
import { Button } from "@/components";
import styles from "./AuthorizeView.module.scss";

const STUDIO_ROLES = new Set(["super_admin", "admin", "3d_designer"]);

type StudioProfile = {
  id: string;
  email: string;
  role: string;
};

type ConfirmPayload = {
  code: string;
  redirectUri: string;
  state: string | null;
};

const redirectToDesktop = (
  redirectUri: string,
  code: string,
  state: string | null,
  error?: string,
) => {
  const custom = new URL(redirectUri);
  if (error) custom.searchParams.set("error", error);
  else custom.searchParams.set("code", code);
  if (state) custom.searchParams.set("state", state);

  const httpsReturn = new URL("/oauth/desktop-return", window.location.origin);
  if (error) httpsReturn.searchParams.set("error", error);
  else httpsReturn.searchParams.set("code", code);
  if (state) httpsReturn.searchParams.set("state", state);

  window.location.replace(custom.toString());
  window.setTimeout(() => {
    window.location.replace(httpsReturn.toString());
  }, 250);
};

/**
 * Studio/desktop OAuth consent surface.
 * Auth continues through central OIDC BFF (opaque host-only cookie) — no
 * credential form and no tokens in browser storage.
 * Confirm/deny never submit userId/accountId; the Worker derives identity
 * from the verified session. Redirect URI + state come from the stored
 * authorization request (not client-forged query values).
 */
export const AuthorizeView = () => {
  const searchParams = useSearchParams();
  const authorizationId = searchParams.get("authorization_id") ?? "";
  const clientId = searchParams.get("client_id") ?? "";

  const [profile, setProfile] = useState<StudioProfile | null>(null);
  const [checking, setChecking] = useState(true);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [done, setDone] = useState(false);
  const completingRef = useRef(false);

  const missingParams = !authorizationId || !clientId;

  const authorizeReturnTo = useMemo(() => {
    const q = searchParams.toString();
    return q ? `/oauth/authorize?${q}` : "/oauth/authorize";
  }, [searchParams]);

  const completeAuthorization = useCallback(async () => {
    if (completingRef.current || !authorizationId) return;
    completingRef.current = true;
    setBusy(true);
    setFormError("");
    try {
      const { data } = await axiosInstance.post("/v2/auth/authorize/confirm", {
        authorizationId,
      });
      const payload = data?.data as ConfirmPayload | undefined;
      if (data?.success && payload?.code && payload?.redirectUri) {
        setDone(true);
        redirectToDesktop(
          payload.redirectUri,
          payload.code,
          payload.state ?? null,
        );
        return;
      }
      completingRef.current = false;
      setFormError(data?.message ?? "Authorization failed");
    } catch {
      completingRef.current = false;
      setFormError("Authorization failed");
    } finally {
      setBusy(false);
    }
  }, [authorizationId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const session = await fetchBffSession();
      if (cancelled) return;
      if (!session) {
        setChecking(false);
        setProfile(null);
        return;
      }
      try {
        // Profile via /v2 (BFF cookie accepted by Worker authMiddleware).
        // Avoid /api/resource/me — that endpoint requires dashboard admin;
        // Studio 3d_designer is valid here but not a dashboard admin.
        const { data } = await axiosInstance.get("/v2/accounts/me");
        if (cancelled) return;
        if (data?.success && data?.data?.id) {
          const user: StudioProfile = {
            id: data.data.id,
            email: data.data.email,
            role: String(data.data.role || ""),
          };
          if (!STUDIO_ROLES.has(user.role)) {
            setProfile(null);
            setChecking(false);
            setFormError(
              "Studio access requires admin or 3D designer role.",
            );
            return;
          }
          setProfile(user);
          setChecking(false);
          void completeAuthorization();
          return;
        }
      } catch {
        // fall through
      }
      if (!cancelled) {
        setProfile(null);
        setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [completeAuthorization]);

  const handleContinueLogin = () => {
    startBffLogin(authorizeReturnTo);
  };

  const handleDeny = async () => {
    setBusy(true);
    try {
      const { data } = await axiosInstance.post("/v2/auth/authorize/deny", {
        authorizationId,
      });
      const redirectUri = data?.data?.redirectUri as string | undefined;
      const state = (data?.data?.state as string | null | undefined) ?? null;
      if (redirectUri) {
        redirectToDesktop(redirectUri, "", state, "access_denied");
        return;
      }
    } catch {
      // ignore
    }
    setBusy(false);
    setFormError("Unable to deny authorization.");
  };

  const handleSwitchAccount = async () => {
    completingRef.current = false;
    setBusy(true);
    setFormError("");
    await logoutBffSession({ global: false });
    setProfile(null);
    setDone(false);
    setBusy(false);
    startBffLogin(authorizeReturnTo);
  };

  if (checking) {
    return (
      <div className={styles["view"]}>
        <h1 className={styles["view__header"]}>Authorize Studio</h1>
        <p className={styles["view__subtitle"]}>Checking session…</p>
      </div>
    );
  }

  if (missingParams) {
    return (
      <div className={styles["view"]}>
        <h1 className={styles["view__header"]}>Authorize Studio</h1>
        <p className={styles["view__subtitle"]}>
          This page must be opened from the Studio desktop app.
        </p>
      </div>
    );
  }

  if (done) {
    return (
      <div className={styles["view"]}>
        <h1 className={styles["view__header"]}>Authorize Studio</h1>
        <p className={styles["view__subtitle"]}>Returning to the desktop app…</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className={styles["view"]}>
        <h1 className={styles["view__header"]}>Authorize Studio</h1>
        <p className={styles["view__subtitle"]}>
          Sign in with a Studio account to continue
        </p>
        <div className={styles["view__form"]}>
          {formError ? <p className={styles["view__body"]}>{formError}</p> : null}
          <Button
            className={styles["view__button"]}
            onClick={handleContinueLogin}
            disabled={busy}
          >
            Continue to sign in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles["view"]}>
      <h1 className={styles["view__header"]}>Authorize Studio</h1>
      <p className={styles["view__subtitle"]}>Signed in as {profile.email}</p>
      <div className={styles["view__form"]}>
        {formError ? <p className={styles["view__body"]}>{formError}</p> : null}
        <p className={styles["view__body"]}>
          Completing authorization for client <strong>{clientId}</strong>…
        </p>
        <Button
          className={styles["view__button"]}
          onClick={() => void completeAuthorization()}
          disabled={busy}
        >
          Authorize
        </Button>
        <Button
          className={styles["view__button"]}
          onClick={() => void handleDeny()}
          disabled={busy}
          variant="secondary"
        >
          Deny
        </Button>
        <Button
          className={styles["view__button"]}
          onClick={() => void handleSwitchAccount()}
          disabled={busy}
          variant="secondary"
        >
          Switch account
        </Button>
      </div>
    </div>
  );
};
